/**
 * Overlays somebody else drew — boundaries, phase lines, areas of interest.
 *
 * A layer is not a track, and the two fields that look optional are where that
 * shows. `validUntil` is null when the author gave no expiry, which is NOT the
 * same as "valid for ever" and must never be rendered as it. `expired` is
 * computed by the server so two screens cannot disagree about whether
 * something on the map is still current — the same rule `Track.stale` follows.
 *
 * Vertices arrive `[lat, lon]`, matching everything else in this system and
 * opposite to the GeoJSON the file was written in. The server does that swap
 * once on the way in; the map does it back once on the way out
 * (`map/useOverlayLayer.ring`). Those are the only two places it happens.
 */

import { api, type ApiResult } from '@/api/client';

/** What a map can draw. A GeoJSON GeometryCollection is flattened into these. */
export type FeatureKind = 'point' | 'line' | 'area';

export interface LayerFeature {
  kind: FeatureKind;
  /**
   * `[lat, lon]` in degrees, in draw order. An area is NOT closed — the last
   * vertex does not repeat the first, matching how AORs are stored, because a
   * duplicate corner is a zero-length edge waiting to trip a calculation.
   */
  vertices: Array<[number, number]>;
  name: string;
  description: string;
}

export interface Layer {
  layerId: string;
  name: string;
  /** Who imported it. Overlays are operator-imported, not feed-ingested. */
  sourceId: string;
  importedAt: number;
  /** Null means the author gave no expiry. Not "for ever". */
  validUntil: number | null;
  /** Computed server-side. Only ever true when an expiry was actually given. */
  expired: boolean;
  featureCount: number;
  features: LayerFeature[];
  notes: string;
}

export interface LayersResponse {
  generatedAt: number;
  layers: Layer[];
  /** False when this server has no external ingest configured at all. */
  configured: boolean;
}

/**
 * What an import did, or would do.
 *
 * `problems` is every problem in the file, not the first: an operator fixing
 * one overlay should not discover the next error on the next attempt. When
 * `accepted` is false nothing was stored — the whole file is refused, because
 * a partially imported overlay is a boundary with a missing edge.
 */
export interface LayerImportResult {
  accepted: boolean;
  dryRun: boolean;
  featureCount: number;
  problems: string[];
  layerId?: string;
  name?: string;
}

export function getLayers(): Promise<ApiResult<LayersResponse>> {
  return api.get<LayersResponse>('/external/layers', { logged: false });
}

/**
 * Send an overlay. `dryRun` reports what it would do and stores nothing —
 * easier to check before it is on everybody's map than after.
 *
 * The format is not passed: the server sniffs GeoJSON from KML, because a
 * browser hands up whatever the file happened to be called.
 */
export function importLayer(input: {
  content: string;
  name?: string;
  validUntil?: number | null;
  notes?: string;
  dryRun?: boolean;
}): Promise<ApiResult<LayerImportResult>> {
  return api.post<LayerImportResult>('/external/layers', input, {
    label: input.dryRun ? 'CHECK OVERLAY' : 'IMPORT OVERLAY',
  });
}

/** A layer will not expire itself, so somebody has to be able to remove it. */
export function deleteLayer(layerId: string): Promise<ApiResult<{ deleted: boolean }>> {
  return api.delete<{ deleted: boolean }>(`/external/layers/${encodeURIComponent(layerId)}`, {
    label: `DELETE OVERLAY ${layerId}`,
  });
}
